import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Eye, EyeOff, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { getApiErrorMessage } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const { login, isLoggingIn } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const clearAuthError = () => {
    if (authError) setAuthError(null)
  }

  const onSubmit = async (data: LoginFormData) => {
    setAuthError(null)
    try {
      await login(data)
      navigate('/')
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : getApiErrorMessage(error, 'Invalid email or password. Please try again.')
      setAuthError(message)
      toast.error(message)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 p-4">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.35),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(251,191,36,0.22),transparent_28%),linear-gradient(180deg,#041411,#0b1b18)]" />
      <div className="relative grid w-full max-w-5xl gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="hidden animate-fade-in text-white lg:flex lg:flex-col lg:justify-center">
          <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-400 text-slate-950">
            <Package className="h-7 w-7" />
          </div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300">Simplogics</p>
          <h1 className="mt-3 max-w-lg text-5xl font-semibold tracking-tight">
            Asset management that stays clear as your inventory grows.
          </h1>
          <p className="mt-4 max-w-md text-base text-emerald-50/75">
            Create one device model, track many assets, and keep company gear separate from project tools.
          </p>
        </div>

        <Card className="animate-fade-in border-white/10 bg-white/95 shadow-2xl backdrop-blur dark:bg-card/95">
          <CardHeader className="space-y-2">
            <div className="mb-1 flex items-center gap-2 lg:hidden">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Package className="h-4 w-4" />
              </div>
              <p className="text-sm font-semibold">Simplogics</p>
            </div>
            <CardTitle className="text-2xl">Welcome back</CardTitle>
            <CardDescription>
              Sign in to Simplogics Asset Management
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {authError && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">Sign-in failed</p>
                    <p className="mt-0.5 text-destructive/90">{authError}</p>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="admin@simplogics.com"
                  aria-invalid={!!errors.email || !!authError}
                  {...register('email', { onChange: clearAuthError })}
                  className={errors.email || authError ? 'h-11 border-destructive' : 'h-11'}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-medium">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    aria-invalid={!!errors.password || !!authError}
                    {...register('password', { onChange: clearAuthError })}
                    className={errors.password || authError ? 'h-11 border-destructive pr-10' : 'h-11 pr-10'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <Button type="submit" className="h-11 w-full" disabled={isLoggingIn}>
                {isLoggingIn ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
